import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusCircle, Pencil, Trash2, ShieldCheck, User, ArrowLeft } from 'lucide-react'
import { getUsers, deleteUser } from '../api/users'
import { useAuth } from '../context/AuthContext'
import styles from './UsersList.module.css'

export default function UsersList() {
  const { token, user: currentUser, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [currentUser, authLoading, navigate])

  useEffect(() => {
    if (!token) return
    getUsers(token)
      .then((all) => setUsers(all.filter((u) => u.source === 'build-with-mak-page')))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false))
  }, [token])

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return
    setDeleting(id)
    try {
      await deleteUser(token, id)
      setUsers((prev) => prev.filter((u) => u.id !== id))
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user.')
    } finally {
      setDeleting(null)
    }
  }

  if (authLoading || !currentUser) return null

  return (
    <main className={styles.page}>
      <Link to="/dashboard" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>
      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>Users</h1>
          <p className={styles.subtitle}>Manage accounts with access to this dashboard</p>
        </div>
        <Link to="/users/new" className={styles.newBtn}>
          <PlusCircle size={16} />
          New User
        </Link>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : users.length === 0 ? (
        <div className={styles.empty}>
          <p>No users yet.</p>
          <Link to="/users/new" className={styles.emptyLink}>
            Create the first user →
          </Link>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className={styles.name}>{u.name}</td>
                  <td className={styles.muted}>@{u.username}</td>
                  <td>
                    <span
                      className={`${styles.roleBadge} ${u.role === 'admin' ? styles.admin : styles.guest}`}
                    >
                      {u.role === 'admin' ? <ShieldCheck size={12} /> : <User size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className={styles.muted}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <Link
                        to={`/users/${u.id}/edit`}
                        className={styles.iconBtn}
                        title="Edit user"
                      >
                        <Pencil size={14} />
                      </Link>
                      {u.id !== currentUser?.id && (
                        <button
                          className={`${styles.iconBtn} ${styles.deleteBtn}`}
                          onClick={() => handleDelete(u.id, u.name)}
                          disabled={deleting === u.id}
                          title="Delete user"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
